import { useState, useCallback, useMemo } from 'react';
import { Crosshair } from 'lucide-react';
import type { Sport, MarketType, DateFilter, StatusFilter, ViewMode, OddsMode } from './types';

function todayMdy(): string {
  const d = new Date();
  return `${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}/${d.getFullYear()}`;
}

function mdyToIso(mdy: string): string {
  const [mm, dd, yyyy] = mdy.split('/');
  return `${yyyy}-${mm}-${dd}`;
}

function shiftMdy(mdy: string, days: number): string {
  const [mm, dd, yyyy] = mdy.split('/').map(Number);
  const d = new Date(yyyy, mm - 1, dd);
  d.setDate(d.getDate() + days);
  return `${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}/${d.getFullYear()}`;
}
import { DashboardToolbar } from './DashboardToolbar';
import { GameTable } from './GameTable';
import { SeriesTable } from './SeriesTable';
import { GameDetailPanel, type TradeIntent } from './GameDetailPanel';
import { TradeModal, type TradeModalData } from './TradeModal';
import { bestPriceCentsForOrderSide } from './tradeModalBook';
import { FeedTape } from './FeedTape';
import { PositionSidebar } from './PositionSidebar';
import { useMarketStore } from '../../stores/marketStore';

export function ValueDashboard() {
  // ── Hydra data (connection managed at App level) ───────────────────
  const connected = useMarketStore((s) => s.connected);
  const marketsCount = useMarketStore((s) => s.marketsCount);
  const orderbooksCount = useMarketStore((s) => s.orderbooksCount);
  const getGames = useMarketStore((s) => s.getGames);
  const getSeries = useMarketStore((s) => s.getSeries);
  const version = useMarketStore((s) => s.version);

  // ── Toolbar state ──────────────────────────────────────────────────
  const [selectedSport, setSelectedSport] = useState<Sport>('nba');
  const [selectedMarketType, setSelectedMarketType] = useState<MarketType>('moneyline');
  const [selectedDate, setSelectedDate] = useState(todayMdy);
  const [dateFilter, setDateFilter] = useState<DateFilter>('today');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('active');
  const [viewMode, setViewMode] = useState<ViewMode>('2-row');
  const [oddsMode] = useState<OddsMode>('maker');

  // ── Feed tape state ─────────────────────────────────────────────────
  const [showFeedTape, setShowFeedTape] = useState(false);

  // ── Position sidebar state ─────────────────────────────────────────
  const [showPositionSidebar, setShowPositionSidebar] = useState(true);

  // ── Data from Hydra via Zustand ─────────────────────────────────────
  // version changes on every RAF tick → forces fresh derivation from mutable maps
  const games = useMemo(() => {
    void version; // explicit dependency
    if (selectedMarketType === 'series') return [];
    return getGames(selectedSport, selectedMarketType, {
      statusFilter,
      dateFilter,
      selectedDateIso: mdyToIso(selectedDate),
    });
  }, [getGames, selectedSport, selectedMarketType, version, statusFilter, dateFilter, selectedDate]);

  const seriesEvent = useMemo(() => {
    void version;
    if (selectedMarketType !== 'series') return null;
    return getSeries(selectedSport);
  }, [getSeries, selectedSport, selectedMarketType, version]);

  // ── Detail panel state ──────────────────────────────────────────────
  const [expandedGameKey, setExpandedGameKey] = useState<string | null>(null);
  const [tradeIntent, setTradeIntent] = useState<TradeIntent | null>(null);

  // ── Trade modal state (opens on odds cell click) ───────────────────
  const [tradeModal, setTradeModal] = useState<TradeModalData | null>(null);

  const handleToggleExpand = useCallback((key: string) => {
    setExpandedGameKey((prev) => (prev === key ? null : key));
    setTradeIntent(null);
  }, []);

  const handleCloseDetail = useCallback(() => {
    setExpandedGameKey(null);
    setTradeIntent(null);
  }, []);

  const handleTradeClick = useCallback(
    (
      gameKey: string,
      side: 'away' | 'home',
      venue: 'kalshi' | 'polymarket',
      opts?: { priceCents?: number; initialOrderSide?: 'yes' | 'no' },
    ) => {
      const game = games.find((g) => g.key === gameKey);
      if (!game) return;

      const initialOrderSide = opts?.initialOrderSide ?? 'yes';
      const book =
        side === 'away'
          ? (venue === 'kalshi' ? game.kalshi?.away : game.polymarket?.away)
          : (venue === 'kalshi' ? game.kalshi?.home : game.polymarket?.home);
      const prefillPrice =
        opts?.priceCents ?? bestPriceCentsForOrderSide(book, initialOrderSide);

      setTradeModal({
        game,
        teamSide: side,
        venue,
        prefillPrice,
        initialOrderSide,
      });

      setExpandedGameKey(gameKey);
      setTradeIntent({ side, venue });
    },
    [games],
  );

  const expandedGame = useMemo(() => {
    if (!expandedGameKey) return null;
    return games.find((g) => g.key === expandedGameKey) ?? null;
  }, [expandedGameKey, games]);

  const seriesTicker = useMemo(() => {
    const map: Record<string, string> = {
      nba: 'KXNBAGAME',
      nhl: 'KXNHLGAME',
      cbb: 'KXNCAAMBGAME',
      atp: 'KXATPMATCH',
      wta: 'KXWTAMATCH',
      mlb: 'KXMLBGAME',
      ufc: 'KXUFCFIGHT',
    };
    return map[selectedSport] ?? '';
  }, [selectedSport]);

  // ── Date navigation ─────────────────────────────────────────────────
  const handleDatePrev = useCallback(() => {
    setSelectedDate((d) => shiftMdy(d, -1));
  }, []);

  const handleDateNext = useCallback(() => {
    setSelectedDate((d) => shiftMdy(d, 1));
  }, []);

  return (
    <div className="flex h-full">
      {/* Main content */}
      <div className="flex flex-1 flex-col overflow-hidden">
        {/* Toolbar */}
        <div className="border-border border-b p-3">
          <div className="flex items-center justify-between">
            <DashboardToolbar
              selectedSport={selectedSport}
              selectedMarketType={selectedMarketType}
              selectedDate={selectedDate}
              dateFilter={dateFilter}
              statusFilter={statusFilter}
              viewMode={viewMode}
              onSportChange={setSelectedSport}
              onMarketTypeChange={setSelectedMarketType}
              onDateChange={setSelectedDate}
              onDatePrev={handleDatePrev}
              onDateNext={handleDateNext}
              onDateFilterChange={setDateFilter}
              onStatusFilterChange={setStatusFilter}
              onViewModeChange={setViewMode}
            />
            {/* Position sidebar toggle */}
            <button
              onClick={() => setShowPositionSidebar((v) => !v)}
              className={`flex items-center gap-1.5 rounded-md px-2 py-1 text-xs transition-colors hover:bg-accent ${
                showPositionSidebar ? 'text-primary' : 'text-muted-foreground'
              }`}
              title="Toggle position tracker"
            >
              <Crosshair size={14} />
            </button>

            {/* Connection status — click to open feed tape */}
            <div className="relative">
              <button
                onClick={() => setShowFeedTape((v) => !v)}
                className="flex items-center gap-2 rounded-md px-2 py-1 text-xs transition-colors hover:bg-accent"
              >
                <span className={`inline-block h-2 w-2 rounded-full ${connected ? 'bg-positive animate-pulse' : 'bg-negative'}`} />
                <span className="text-muted-foreground">
                  {connected ? `${marketsCount} mkts / ${orderbooksCount} books` : 'Disconnected'}
                </span>
              </button>
              {showFeedTape && <FeedTape onClose={() => setShowFeedTape(false)} />}
            </div>
          </div>
        </div>

        {/* Game / Series table */}
        <div className="flex-1 overflow-auto p-4">
          {selectedMarketType === 'series' ? (
            seriesEvent ? (
              <SeriesTable event={seriesEvent} />
            ) : (
              <div className="text-muted-foreground flex h-64 flex-col items-center justify-center gap-2">
                {connected ? (
                  <span>No {selectedSport.toUpperCase()} series markets</span>
                ) : (
                  <>
                    <div className="border-border border-t-primary h-5 w-5 animate-spin rounded-full border-2" />
                    <span>Connecting to Hydra...</span>
                  </>
                )}
              </div>
            )
          ) : games.length > 0 ? (
            <GameTable
              games={games}
              seriesTicker={seriesTicker}
              marketType={selectedMarketType}
              oddsMode={oddsMode}
              expandedGameKey={expandedGameKey}
              onToggleExpand={handleToggleExpand}
              onTradeClick={handleTradeClick}
            />
          ) : (
            <div className="text-muted-foreground flex h-64 flex-col items-center justify-center gap-2">
              {connected ? (
                <span>No {selectedSport.toUpperCase()} games</span>
              ) : (
                <>
                  <div className="border-border border-t-primary h-5 w-5 animate-spin rounded-full border-2" />
                  <span>Connecting to Hydra...</span>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Detail panel (slides in from right) — hides position sidebar */}
      {expandedGame && (
        <GameDetailPanel game={expandedGame} onClose={handleCloseDetail} tradeIntent={tradeIntent} />
      )}

      {/* Position sidebar (persistent, hidden when detail panel open) */}
      {!expandedGame && showPositionSidebar && (
        <PositionSidebar onClose={() => setShowPositionSidebar(false)} />
      )}

      {/* Trade modal (overlays on odds cell click) */}
      {tradeModal && (
        <TradeModal
          key={`${tradeModal.game.key}-${tradeModal.teamSide}-${tradeModal.venue}-${tradeModal.prefillPrice ?? ''}-${tradeModal.initialOrderSide ?? 'yes'}`}
          data={tradeModal}
          onClose={() => setTradeModal(null)}
        />
      )}
    </div>
  );
}
